import { useMemo, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';

import type { PreloadedSearchPayload } from './preloadSearch.ts';

export const usePreloadDataProvider = (): {
	getPreloadedSearch: ({ cloudId }: { cloudId: string }) => PreloadedSearchPayload | null;
	clearPreloadedSearch: ({ cloudId }: { cloudId: string }) => void;
} => {
	const isTransition = useIsTransition();
	// Immediate runtime guard for non-initial-load mode.
	const isTransitionRef = useRef(isTransition);
	isTransitionRef.current = isTransition;
	// Sticky provider-local retirement flag for explicitly consumed preload data.
	const clearedRef = useRef(false);

	return useMemo(
		() => ({
			getPreloadedSearch: ({ cloudId }: { cloudId: string }): PreloadedSearchPayload | null => {
				// When rovo_search_preload_from_quick_find_using_async is on, we relax two
				// previously-strict guards so the preloaded payload can be reused to warm up
				// Quick Find:
				//   1. We no longer bail out during route transitions.
				//   2. We no longer prevent the payload from being returned after it has been
				//      explicitly consumed (the `clearedRef` flag). The consumer (search-page)
				//      is now responsible for clearing it at the right times.

				if (
					!expVal('rovo_search_preload_from_quick_find_using_async', 'isEnabled', false) &&
					isTransitionRef.current
				) {
					return null;
				}

				const preloadedSearch = window.__PRELOADED_SEARCH__;

				if (!preloadedSearch || preloadedSearch.cloudId !== cloudId) {
					return null;
				}

				if (
					!expVal('rovo_search_preload_from_quick_find_using_async', 'isEnabled', false) &&
					clearedRef.current
				) {
					return null;
				}

				return preloadedSearch as PreloadedSearchPayload;
			},
			clearPreloadedSearch: ({ cloudId }: { cloudId: string }) => {
				const preloadedSearch = window.__PRELOADED_SEARCH__;

				if (preloadedSearch?.cloudId === cloudId) {
					if (expVal('rovo_search_preload_from_quick_find_using_async', 'isEnabled', false)) {
						window.__PRELOADED_SEARCH__ = undefined;
					}

					clearedRef.current = true;
				}
			},
		}),
		[],
	);
};
